/**
 * Asset references for the hard-coded content layer.
 *
 * Content modules never hold image URLs directly. They hold a marker created by
 * `asset()`, naming a file in src/content/images/. At render time
 * `useSiteContent()` swaps each marker for a real object carrying `url` and
 * `fluid`, so components keep using `<Img fluid={...} />` and `src={...url}`
 * exactly as they did when the data came from DatoCMS.
 *
 *   heroImg: asset('1653899396-yleiset-tilat-8.jpg', { alt: 'Aula' })
 */

/**
 * @param {string} file  filename inside src/content/images/
 * @param {object} [meta] alt, title, width, height, sizes, aspectRatio
 */
const asset = (file, meta = {}) => ({ __asset: true, file, ...meta });

const isAsset = value =>
  !!value && typeof value === 'object' && value.__asset === true;

// Shown when an asset is referenced but the file is not in src/content/images/.
// A visible neutral block beats a crash: several originals were lost with the
// DatoCMS account and are still being recovered (see scripts/recover-assets.js).
const PLACEHOLDER =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="9">' +
      '<rect width="16" height="9" fill="#d8d2c6"/></svg>'
  );

const placeholderFluid = meta => ({
  base64: PLACEHOLDER,
  src: PLACEHOLDER,
  srcSet: '',
  sizes: meta.sizes || '100vw',
  aspectRatio: meta.aspectRatio || 16 / 9,
});

/**
 * Resolve one marker against the map of files Gatsby found on disk.
 * @param {object} marker     result of asset()
 * @param {Map}    fileNodes  filename -> gatsby File node
 */
const resolveAsset = (marker, fileNodes) => {
  const { __asset, file, sizes, aspectRatio, ...meta } = marker;
  const node = fileNodes.get(file);
  const sharpFluid = node && node.childImageSharp && node.childImageSharp.fluid;

  return {
    ...meta,
    file,
    missing: !node,
    url: node ? node.publicURL : PLACEHOLDER,
    // Keep the `sizes` the original design asked for; gatsby-plugin-sharp only
    // knows the slot width we compiled with, not the one the layout uses.
    fluid: sharpFluid
      ? { ...sharpFluid, ...(sizes ? { sizes } : {}) }
      : placeholderFluid({ sizes, aspectRatio }),
  };
};

/**
 * Walk a content tree and replace every asset marker with a resolved object.
 * Plain values pass through untouched.
 */
const resolveAssets = (value, fileNodes) => {
  if (isAsset(value)) return resolveAsset(value, fileNodes);
  if (Array.isArray(value)) return value.map(v => resolveAssets(v, fileNodes));
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value)) {
      out[key] = resolveAssets(value[key], fileNodes);
    }
    return out;
  }
  return value;
};

module.exports = { asset, isAsset, resolveAsset, resolveAssets };
